/**
 * @fileoverview Hot Module Reload (HMR) support with state preservation
 */

import { EventEmitter } from 'eventemitter3'

/**
 * HMR configuration options
 */
export interface HMROptions {
  /** Enable HMR functionality */
  enabled?: boolean
  /** Preserve component state during reload */
  preserveState?: boolean
  /** HMR server port (usually handled by dev server) */
  port?: number
  /** WebSocket connection options */
  websocket?: {
    /** Custom WebSocket URL */
    url?: string
    /** Connection timeout in milliseconds */
    timeout?: number
    /** Reconnection attempts */
    maxReconnects?: number
  }
  /** File watching options */
  watch?: {
    /** File patterns to watch */
    include?: string[]
    /** File patterns to ignore */
    exclude?: string[]
    /** Debounce delay in milliseconds */
    debounceMs?: number
  }
  /** State preservation options */
  state?: {
    /** State keys to preserve */
    preserveKeys?: string[]
    /** State keys to reset */
    resetKeys?: string[]
    /** Custom state serializer */
    serialize?: (state: any) => string
    /** Custom state deserializer */
    deserialize?: (data: string) => any
  }
}

/**
 * HMR update information
 */
export interface HMRUpdate {
  /** Update type */
  type: 'full-reload' | 'css-update' | 'js-update' | 'component-update' | 'asset-update'
  /** Updated file paths */
  files: string[]
  /** Timestamp of the update */
  timestamp: number
  /** Update details */
  details?: {
    /** Component names that were updated */
    components?: string[]
    /** CSS selectors that were updated */
    cssRules?: string[]
    /** Assets that were updated */
    assets?: string[]
  }
  /** Whether state should be preserved */
  preserveState?: boolean
}

/**
 * HMR state snapshot
 */
export interface HMRStateSnapshot {
  /** Snapshot timestamp */
  timestamp: number
  /** Preserved state data */
  state: Record<string, any>
  /** Component instance data */
  components?: Record<string, any>
  /** Form data */
  forms?: Record<string, any>
  /** Scroll positions */
  scroll?: Record<string, { x: number; y: number }>
  /** Local storage data */
  localStorage?: Record<string, string>
  /** Session storage data */
  sessionStorage?: Record<string, string>
}

/**
 * Hot Module Reload manager
 */
export class HMRManager extends EventEmitter {
  private httpClient: any
  private wsClient?: any
  private sandboxId: string
  private options: HMROptions
  private _enabled = false
  private _connected = false
  private stateSnapshot?: HMRStateSnapshot
  private reconnectAttempts = 0
  private reconnectTimer?: NodeJS.Timeout

  constructor(httpClient: any, wsClient: any, sandboxId: string, options: HMROptions = {}) {
    super()
    this.httpClient = httpClient
    this.wsClient = wsClient
    this.sandboxId = sandboxId
    this.options = {
      enabled: true,
      preserveState: true,
      port: 24678, // Standard HMR port
      websocket: {
        timeout: 5000,
        maxReconnects: 10
      },
      watch: {
        include: ['**/*.{js,jsx,ts,tsx,vue,svelte}', '**/*.css', '**/*.scss', '**/*.less'],
        exclude: ['**/node_modules/**', '**/dist/**', '**/.git/**'],
        debounceMs: 100
      },
      state: {
        preserveKeys: ['user', 'auth', 'settings', 'form-data'],
        resetKeys: ['error', 'loading', 'temp']
      },
      ...options
    }
  }

  /**
   * Enable HMR functionality
   */
  async enable(): Promise<void> {
    if (this._enabled) {
      return
    }

    try {
      // Initialize HMR on the sandbox
      const response = await this.httpClient.post(
        `/api/sandbox/${this.sandboxId}/hmr/enable`,
        {
          options: this.options
        }
      )

      if (!response.success) {
        throw new Error(`Failed to enable HMR: ${response.error}`)
      }

      this._enabled = true

      // Set up WebSocket connection for HMR events
      if (this.wsClient) {
        this.setupWebSocketListeners()
        await this.connectHMR()
      }

      this.emit('enabled')
    } catch (error) {
      throw new Error(`HMR enable failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  /**
   * Disable HMR functionality
   */
  async disable(): Promise<void> {
    if (!this._enabled) {
      return
    }

    try {
      const response = await this.httpClient.post(
        `/api/sandbox/${this.sandboxId}/hmr/disable`
      )

      if (!response.success) {
        console.warn(`Failed to disable HMR: ${response.error}`)
      }

      this._enabled = false
      this._connected = false

      // Clean up WebSocket listeners
      if (this.wsClient) {
        this.cleanupWebSocketListeners()
      }

      // Clear reconnect timer
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer)
        this.reconnectTimer = undefined
      }

      this.emit('disabled')
    } catch (error) {
      console.warn(`HMR disable failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  /**
   * Create state snapshot for preservation
   */
  createStateSnapshot(): HMRStateSnapshot {
    const snapshot: HMRStateSnapshot = {
      timestamp: Date.now(),
      state: {},
      components: {},
      forms: {},
      scroll: {},
      localStorage: {},
      sessionStorage: {}
    }

    // Capture state based on framework
    if (typeof window !== 'undefined') {
      // Browser environment - capture DOM state
      this.captureFormState(snapshot)
      this.captureScrollState(snapshot)
      this.captureStorageState(snapshot)
      this.captureComponentState(snapshot)
    }

    this.stateSnapshot = snapshot
    this.emit('state-captured', snapshot)

    return snapshot
  }

  /**
   * Restore state from snapshot
   */
  restoreStateSnapshot(snapshot?: HMRStateSnapshot): void {
    const targetSnapshot = snapshot || this.stateSnapshot
    if (!targetSnapshot) {
      return
    }

    if (typeof window !== 'undefined') {
      // Browser environment - restore DOM state
      this.restoreFormState(targetSnapshot)
      this.restoreScrollState(targetSnapshot)
      this.restoreStorageState(targetSnapshot)
      this.restoreComponentState(targetSnapshot)
    }

    this.emit('state-restored', targetSnapshot)
  }

  /**
   * Handle HMR update
   */
  async handleUpdate(update: HMRUpdate): Promise<void> {
    this.emit('update-start', update)

    try {
      // Create state snapshot if preservation is enabled
      if (this.options.preserveState && update.preserveState !== false) {
        this.createStateSnapshot()
      }

      // Handle different update types
      switch (update.type) {
        case 'css-update':
          await this.handleCSSUpdate(update)
          break
        case 'js-update':
          await this.handleJSUpdate(update)
          break
        case 'component-update':
          await this.handleComponentUpdate(update)
          break
        case 'asset-update':
          await this.handleAssetUpdate(update)
          break
        case 'full-reload':
        default:
          await this.handleFullReload(update)
          break
      }

      this.emit('update-complete', update)
    } catch (error) {
      this.emit('update-error', { update, error })
      throw error
    }
  }

  /**
   * Handle CSS updates (hot swap without reload)
   */
  private async handleCSSUpdate(update: HMRUpdate): Promise<void> {
    if (typeof document === 'undefined') {
      return
    }

    for (const file of update.files) {
      // Find existing stylesheets
      const links = document.querySelectorAll(`link[href*="${file}"]`)

      Array.from(links).forEach(link => {
        const href = (link as HTMLLinkElement).href
        const newHref = `${href.split('?')[0]}?t=${Date.now()}`
        ;(link as HTMLLinkElement).href = newHref
      })

      // Handle style tags
      const styles = document.querySelectorAll(`style[data-file="${file}"]`)
      Array.from(styles).forEach(async style => {
        // Request updated CSS content
        try {
          const response = await this.httpClient.get(`/api/sandbox/${this.sandboxId}/files`, {
            path: file
          })

          if (response.success) {
            style.textContent = response.data.content
          }
        } catch (error) {
          console.warn(`Failed to update CSS file ${file}:`, error)
        }
      })
    }
  }

  /**
   * Handle JavaScript updates
   */
  private async handleJSUpdate(update: HMRUpdate): Promise<void> {
    // For JS updates, we typically need to reload or use framework-specific HMR
    if (this.detectFramework() === 'react') {
      await this.handleReactHMR(update)
    } else if (this.detectFramework() === 'vue') {
      await this.handleVueHMR(update)
    } else {
      // Default: trigger full reload for JS changes
      await this.handleFullReload(update)
    }
  }

  /**
   * Handle component updates
   */
  private async handleComponentUpdate(update: HMRUpdate): Promise<void> {
    const framework = this.detectFramework()

    switch (framework) {
      case 'react':
        await this.handleReactComponentUpdate(update)
        break
      case 'vue':
        await this.handleVueComponentUpdate(update)
        break
      case 'svelte':
        await this.handleSvelteComponentUpdate(update)
        break
      default:
        await this.handleFullReload(update)
    }
  }

  /**
   * Handle asset updates
   */
  private async handleAssetUpdate(update: HMRUpdate): Promise<void> {
    if (typeof document === 'undefined') {
      return
    }

    for (const file of update.files) {
      // Update images
      const images = document.querySelectorAll(`img[src*="${file}"]`)
      Array.from(images).forEach(img => {
        const src = (img as HTMLImageElement).src
        ;(img as HTMLImageElement).src = `${src.split('?')[0]}?t=${Date.now()}`
      })

      // Update other assets (videos, audio, etc.)
      const assets = document.querySelectorAll(`[src*="${file}"], [href*="${file}"]`)
      Array.from(assets).forEach(asset => {
        if (asset.hasAttribute('src')) {
          const src = asset.getAttribute('src')!
          asset.setAttribute('src', `${src.split('?')[0]}?t=${Date.now()}`)
        }
        if (asset.hasAttribute('href')) {
          const href = asset.getAttribute('href')!
          asset.setAttribute('href', `${href.split('?')[0]}?t=${Date.now()}`)
        }
      })
    }
  }

  /**
   * Handle full page reload with state preservation
   */
  private async handleFullReload(update: HMRUpdate): Promise<void> {
    if (typeof window !== 'undefined') {
      // Store state before reload
      if (this.options.preserveState && this.stateSnapshot) {
        sessionStorage.setItem('__hmr_state__', JSON.stringify(this.stateSnapshot))
      }

      // Perform reload
      window.location.reload()
    }
  }

  /**
   * Setup WebSocket listeners for HMR events
   */
  private setupWebSocketListeners(): void {
    if (!this.wsClient) return

    this.wsClient.on('hmr-update', (data: HMRUpdate) => {
      this.handleUpdate(data).catch(error => {
        this.emit('error', error)
      })
    })

    this.wsClient.on('hmr-connected', () => {
      this._connected = true
      this.reconnectAttempts = 0
      this.emit('connected')
    })

    this.wsClient.on('hmr-disconnected', () => {
      this._connected = false
      this.emit('disconnected')
      this.scheduleReconnect()
    })

    this.wsClient.on('hmr-error', (error: any) => {
      this.emit('error', error)
    })
  }

  /**
   * Cleanup WebSocket listeners
   */
  private cleanupWebSocketListeners(): void {
    if (!this.wsClient) return

    this.wsClient.off('hmr-update')
    this.wsClient.off('hmr-connected')
    this.wsClient.off('hmr-disconnected')
    this.wsClient.off('hmr-error')
  }

  /**
   * Connect to HMR WebSocket
   */
  private async connectHMR(): Promise<void> {
    if (!this.wsClient) {
      throw new Error('WebSocket client not available')
    }

    // Join HMR room
    this.wsClient.send({
      event: 'hmr-join',
      data: { sandboxId: this.sandboxId },
      sandboxId: this.sandboxId
    })
  }

  /**
   * Schedule reconnection attempt
   */
  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= (this.options.websocket?.maxReconnects || 10)) {
      this.emit('max-reconnects-exceeded')
      return
    }

    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000) // Exponential backoff, max 30s
    this.reconnectAttempts++

    this.reconnectTimer = setTimeout(async () => {
      try {
        await this.connectHMR()
      } catch (error) {
        this.emit('reconnect-failed', error)
        this.scheduleReconnect()
      }
    }, delay)
  }

  /**
   * Detect the framework being used
   */
  private detectFramework(): string {
    if (typeof window === 'undefined') {
      return 'unknown'
    }

    // Check for React
    if ((window as any).React || document.querySelector('[data-reactroot]')) {
      return 'react'
    }

    // Check for Vue
    if ((window as any).Vue || document.querySelector('[data-v-]')) {
      return 'vue'
    }

    // Check for Svelte
    if (document.querySelector('[data-svelte]')) {
      return 'svelte'
    }

    return 'vanilla'
  }

  /**
   * Framework-specific HMR handlers
   */
  private async handleReactHMR(update: HMRUpdate): Promise<void> {
    // React Fast Refresh integration
    if ((window as any).__REACT_DEVTOOLS_GLOBAL_HOOK__) {
      // Trigger React Fast Refresh
      const ReactRefreshRuntime = (window as any).__webpack_require__?.cache?.[Symbol.for('react.refresh')]
      if (ReactRefreshRuntime) {
        ReactRefreshRuntime.performReactRefresh()
      }
    } else {
      await this.handleFullReload(update)
    }
  }

  private async handleVueHMR(update: HMRUpdate): Promise<void> {
    // Vue HMR integration
    if ((window as any).__VUE_HMR_RUNTIME__) {
      const runtime = (window as any).__VUE_HMR_RUNTIME__
      for (const file of update.files) {
        runtime.updateStyle(file)
      }
    } else {
      await this.handleFullReload(update)
    }
  }

  private async handleReactComponentUpdate(update: HMRUpdate): Promise<void> {
    // React component-specific updates
    await this.handleReactHMR(update)
  }

  private async handleVueComponentUpdate(update: HMRUpdate): Promise<void> {
    // Vue component-specific updates
    await this.handleVueHMR(update)
  }

  private async handleSvelteComponentUpdate(update: HMRUpdate): Promise<void> {
    // Svelte HMR integration
    if ((window as any).__SVELTE_HMR__) {
      // Svelte has its own HMR system
      return
    }
    await this.handleFullReload(update)
  }

  /**
   * State capture and restoration methods
   */
  private captureFormState(snapshot: HMRStateSnapshot): void {
    const forms = document.querySelectorAll('form')
    forms.forEach((form, index) => {
      const data: Record<string, any> = {}

      // Iterate over form elements to extract values
      const elements = form.elements
      for (let i = 0; i < elements.length; i++) {
        const element = elements[i] as HTMLInputElement
        if (element.name && element.value !== undefined) {
          data[element.name] = element.value
        }
      }

      snapshot.forms![`form-${index}`] = data
    })

    // Capture individual input values
    const inputs = document.querySelectorAll('input, textarea, select')
    inputs.forEach((input, index) => {
      const element = input as HTMLInputElement
      if (element.name || element.id) {
        const key = element.name || element.id || `input-${index}`
        snapshot.forms![key] = element.value
      }
    })
  }

  private restoreFormState(snapshot: HMRStateSnapshot): void {
    if (!snapshot.forms) return

    Object.entries(snapshot.forms).forEach(([key, value]) => {
      if (key.startsWith('form-')) {
        // Restore form data
        const formIndex = parseInt(key.split('-')[1])
        const form = document.querySelectorAll('form')[formIndex]
        if (form && typeof value === 'object') {
          Object.entries(value).forEach(([fieldName, fieldValue]) => {
            const field = form.querySelector(`[name="${fieldName}"]`) as HTMLInputElement
            if (field) field.value = fieldValue as string
          })
        }
      } else {
        // Restore individual input
        const input = document.querySelector(`[name="${key}"], [id="${key}"]`) as HTMLInputElement
        if (input) input.value = value as string
      }
    })
  }

  private captureScrollState(snapshot: HMRStateSnapshot): void {
    // Main window scroll
    snapshot.scroll!['window'] = {
      x: window.scrollX,
      y: window.scrollY
    }

    // Scrollable elements
    const scrollableElements = document.querySelectorAll('[data-scrollable], .scroll, .overflow-auto')
    scrollableElements.forEach((element, index) => {
      const key = element.id || `scrollable-${index}`
      snapshot.scroll![key] = {
        x: element.scrollLeft,
        y: element.scrollTop
      }
    })
  }

  private restoreScrollState(snapshot: HMRStateSnapshot): void {
    if (!snapshot.scroll) return

    Object.entries(snapshot.scroll).forEach(([key, position]) => {
      if (key === 'window') {
        window.scrollTo(position.x, position.y)
      } else {
        const element = document.getElementById(key) ||
                      document.querySelector(`[data-scrollable="${key}"]`)
        if (element) {
          element.scrollLeft = position.x
          element.scrollTop = position.y
        }
      }
    })
  }

  private captureStorageState(snapshot: HMRStateSnapshot): void {
    // Capture localStorage
    for (const key in localStorage) {
      if (this.shouldPreserveStorageKey(key)) {
        snapshot.localStorage![key] = localStorage.getItem(key) || ''
      }
    }

    // Capture sessionStorage
    for (const key in sessionStorage) {
      if (this.shouldPreserveStorageKey(key)) {
        snapshot.sessionStorage![key] = sessionStorage.getItem(key) || ''
      }
    }
  }

  private restoreStorageState(snapshot: HMRStateSnapshot): void {
    // Restore localStorage
    if (snapshot.localStorage) {
      Object.entries(snapshot.localStorage).forEach(([key, value]) => {
        localStorage.setItem(key, value)
      })
    }

    // Restore sessionStorage
    if (snapshot.sessionStorage) {
      Object.entries(snapshot.sessionStorage).forEach(([key, value]) => {
        sessionStorage.setItem(key, value)
      })
    }
  }

  private captureComponentState(snapshot: HMRStateSnapshot): void {
    // Framework-specific component state capture
    const framework = this.detectFramework()

    switch (framework) {
      case 'react':
        this.captureReactState(snapshot)
        break
      case 'vue':
        this.captureVueState(snapshot)
        break
      // Add other frameworks as needed
    }
  }

  private restoreComponentState(snapshot: HMRStateSnapshot): void {
    // Framework-specific component state restoration
    const framework = this.detectFramework()

    switch (framework) {
      case 'react':
        this.restoreReactState(snapshot)
        break
      case 'vue':
        this.restoreVueState(snapshot)
        break
      // Add other frameworks as needed
    }
  }

  private captureReactState(snapshot: HMRStateSnapshot): void {
    // React-specific state capture would require integration with React DevTools
    // This is a placeholder for React state capture
  }

  private restoreReactState(snapshot: HMRStateSnapshot): void {
    // React-specific state restoration
    // This is a placeholder for React state restoration
  }

  private captureVueState(snapshot: HMRStateSnapshot): void {
    // Vue-specific state capture
    if ((window as any).__VUE_DEVTOOLS_GLOBAL_HOOK__) {
      // Integration with Vue DevTools for state capture
    }
  }

  private restoreVueState(snapshot: HMRStateSnapshot): void {
    // Vue-specific state restoration
  }

  private shouldPreserveStorageKey(key: string): boolean {
    const preserveKeys = this.options.state?.preserveKeys || []
    const resetKeys = this.options.state?.resetKeys || []

    // Don't preserve keys that are explicitly marked for reset
    if (resetKeys.some(pattern => key.includes(pattern))) {
      return false
    }

    // Preserve keys that match preserve patterns
    if (preserveKeys.length === 0) {
      return true // Preserve all by default
    }

    return preserveKeys.some(pattern => key.includes(pattern))
  }

  /**
   * Get HMR status
   */
  get enabled(): boolean {
    return this._enabled
  }

  get connected(): boolean {
    return this._connected
  }

  get reconnectCount(): number {
    return this.reconnectAttempts
  }
}